import { useCallback, useEffect, useMemo, useState } from 'react';
import DataGrid, { Column, Scrolling, Sorting, HeaderFilter } from 'devextreme-react/data-grid';
import Button from 'devextreme-react/button';
import { DateBox } from 'devextreme-react/date-box';
import { SelectBox } from 'devextreme-react/select-box';
import TabPanel, { Item as TabItem } from 'devextreme-react/tab-panel';
import { Chart, Series, ArgumentAxis, ValueAxis, Legend, Tooltip as ChartTooltip, CommonSeriesSettings, Point, Animation, ConstantLine, Label } from 'devextreme-react/chart';
import LoadPanel from 'devextreme-react/load-panel';
import ScrollView from 'devextreme-react/scroll-view';
import { useApiMeasurementsQC } from '../../api/api.measurementsQC';
import {
  ConfiguracionQcDTO,
  QcReportFilterParams,
  SigmaPerformanceReportDTO,
  CvPerformanceReportDTO,
  GeneralInfoRowDTO,
  IndicatorAnalitoDTO,
  SigmaHistoryRowDTO,
  CvHistoryRowDTO,
  QcPlanningRowDTO,
} from '../../types/types.qualityControl';
import { newToast } from '../../components';
import { ConvertLocalDate } from '../../utils/functions';
import { generateConsolidatedReportPdf } from './qc-reports-pdf';

// ─── Colores de las bandas Sigma ───
const SIGMA_BAND_COLORS = {
  WORLD: '#4CAF50',
  EXCELLENT: '#2196F3',
  GOOD: '#FFC107',
  MIN_ACCEPT: '#FF9800',
  UNACCEPT: '#F44336',
  CRITICAL: '#B71C1C',
};

const sigmaBandColor = (sigma: number | null | undefined): string => {
  if (sigma == null || isNaN(sigma)) return '#9E9E9E';
  if (sigma >= 6) return SIGMA_BAND_COLORS.WORLD;
  if (sigma >= 5) return SIGMA_BAND_COLORS.EXCELLENT;
  if (sigma >= 4) return SIGMA_BAND_COLORS.GOOD;
  if (sigma >= 3) return SIGMA_BAND_COLORS.MIN_ACCEPT;
  if (sigma >= 2) return SIGMA_BAND_COLORS.UNACCEPT;
  return SIGMA_BAND_COLORS.CRITICAL;
};

const indexColor = (val: number | null | undefined): string => {
  if (val == null || isNaN(val)) return '#9E9E9E';
  return val <= 1.0 ? '#4CAF50' : '#F44336';
};

interface QcReportsProps {
  configuraciones: ConfiguracionQcDTO[] | null;
  defaultSedeId: number;
}

type ReportTab = 'sigma' | 'cv' | 'general' | 'indicators' | 'sigmaHist' | 'cvHist' | 'planning';

const QcReports = ({ configuraciones, defaultSedeId }: QcReportsProps) => {
  const {
    getSigmaPerformance,
    getCvPerformance,
    getGeneralInfo,
    getIndicators,
    getSigmaHistory,
    getCvHistory,
    getQcPlanning,
    getConsolidatedReport,
  } = useApiMeasurementsQC();

  // ─── Filtros ───
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const threeMonthsAgo = useMemo(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 3);
    d.setDate(1);
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const [fechaDesde, setFechaDesde] = useState<Date>(threeMonthsAgo);
  const [fechaHasta, setFechaHasta] = useState<Date>(today);
  const [analizadorId, setAnalizadorId] = useState<number | null>(null);
  const [loteId, setLoteId] = useState<number | null>(null);
  const [analitoId, setAnalitoId] = useState<number | null>(null);

  const [activeTab, setActiveTab] = useState<ReportTab>('sigma');
  const [hasSearched, setHasSearched] = useState(false);

  // ─── Datos por reporte ───
  const [sigmaData, setSigmaData] = useState<SigmaPerformanceReportDTO | null>(null);
  const [cvData, setCvData] = useState<CvPerformanceReportDTO | null>(null);
  const [generalData, setGeneralData] = useState<GeneralInfoRowDTO[]>([]);
  const [indicatorsData, setIndicatorsData] = useState<IndicatorAnalitoDTO[]>([]);
  const [sigmaHistData, setSigmaHistData] = useState<SigmaHistoryRowDTO[]>([]);
  const [cvHistData, setCvHistData] = useState<CvHistoryRowDTO[]>([]);
  const [planningData, setPlanningData] = useState<QcPlanningRowDTO[]>([]);

  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // ─── Opciones de los selectores derivadas de configuraciones ───
  const analizadorOptions = useMemo(() => {
    if (!configuraciones) return [];
    const map = new Map<number, string>();
    configuraciones.forEach((c) => {
      if (c.Qcc_Az_Id && c.AnalizadorNombre && !map.has(c.Qcc_Az_Id)) {
        map.set(c.Qcc_Az_Id, c.AnalizadorNombre);
      }
    });
    return Array.from(map.entries()).map(([id, nombre]) => ({ id, nombre }));
  }, [configuraciones]);

  const loteOptions = useMemo(() => {
    if (!configuraciones) return [];
    const map = new Map<number, string>();
    configuraciones.forEach((c) => {
      if (c.Qcc_Qcl_Id && c.LoteCodigo && !map.has(c.Qcc_Qcl_Id)) {
        map.set(c.Qcc_Qcl_Id, `${c.LoteCodigo}${c.MaterialNombre ? ` (${c.MaterialNombre})` : ''}`);
      }
    });
    return Array.from(map.entries()).map(([id, nombre]) => ({ id, nombre }));
  }, [configuraciones]);

  const analitoOptions = useMemo(() => {
    if (!configuraciones) return [];
    const map = new Map<number, string>();
    configuraciones.forEach((c) => {
      if (c.Qcc_An_Id && c.AnalitoNombre && !map.has(c.Qcc_An_Id)) {
        map.set(c.Qcc_An_Id, c.AnalitoNombre);
      }
    });
    return Array.from(map.entries()).map(([id, nombre]) => ({ id, nombre })).sort((a, b) => a.nombre.localeCompare(b.nombre));
  }, [configuraciones]);

  // ─── Construir filtro ───
  const buildFilter = useCallback((): QcReportFilterParams | null => {
    if (!fechaDesde || !fechaHasta) {
      newToast('ERROR', 'Debe seleccionar el rango de fechas', 4);
      return null;
    }
    if (fechaDesde > fechaHasta) {
      newToast('ERROR', 'La fecha desde no puede ser mayor a la fecha hasta', 4);
      return null;
    }
    return {
      FechaDesde: ConvertLocalDate(fechaDesde),
      FechaHasta: ConvertLocalDate(fechaHasta),
      SedeId: defaultSedeId || null,
      AnalizadorId: analizadorId,
      LoteId: loteId,
      AnalitoId: analitoId,
    };
  }, [fechaDesde, fechaHasta, defaultSedeId, analizadorId, loteId, analitoId]);

  // ─── Cargar reporte por tab ───
  const loadReport = useCallback(async (tab: ReportTab, filter: QcReportFilterParams) => {
    setIsLoading(true);
    try {
      switch (tab) {
        case 'sigma': {
          const r = await getSigmaPerformance(filter);
          setSigmaData(r);
          break;
        }
        case 'cv': {
          const r = await getCvPerformance(filter);
          setCvData(r);
          break;
        }
        case 'general': {
          const r = await getGeneralInfo(filter);
          setGeneralData(r || []);
          break;
        }
        case 'indicators': {
          const r = await getIndicators(filter);
          setIndicatorsData(r || []);
          break;
        }
        case 'sigmaHist': {
          const r = await getSigmaHistory(filter);
          setSigmaHistData(r || []);
          break;
        }
        case 'cvHist': {
          const r = await getCvHistory(filter);
          setCvHistData(r || []);
          break;
        }
        case 'planning': {
          const r = await getQcPlanning(filter);
          setPlanningData(r || []);
          break;
        }
      }
    } catch (err) {
      console.error('Error cargando informe:', err);
      newToast('ERROR', 'Error al cargar el informe', 5);
    } finally {
      setIsLoading(false);
    }
  }, [getSigmaPerformance, getCvPerformance, getGeneralInfo, getIndicators, getSigmaHistory, getCvHistory, getQcPlanning]);

  // ─── Generar / refrescar al hacer click ───
  const handleGenerate = useCallback(async () => {
    const filter = buildFilter();
    if (!filter) return;
    setHasSearched(true);
    await loadReport(activeTab, filter);
  }, [buildFilter, activeTab, loadReport]);

  // ─── Exportar PDF consolidado (todos los informes en un solo archivo) ───
  const handleExportPdf = useCallback(async () => {
    const filter = buildFilter();
    if (!filter) return;
    setIsExporting(true);
    try {
      const consolidated = await getConsolidatedReport(filter);
      if (!consolidated) {
        newToast('INFO', 'No se encontraron datos para el período/filtros seleccionados', 4);
        return;
      }
      generateConsolidatedReportPdf(consolidated);
      newToast('SUCCESS', 'Informe PDF generado correctamente', 3);
    } catch (err) {
      console.error('Error generando PDF:', err);
      newToast('ERROR', 'Error al generar el PDF', 5);
    } finally {
      setIsExporting(false);
    }
  }, [buildFilter, getConsolidatedReport]);

  // ─── Cambio de tab — recargar si ya se buscó ───
  useEffect(() => {
    if (!hasSearched) return;
    const filter = buildFilter();
    if (!filter) return;
    loadReport(activeTab, filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // ─── Render: Resumen Sigma (scatter + tabla) ───
  const renderSigmaTab = () => {
    if (!sigmaData) return <div className='qc-reports-empty'>Genere el informe para ver los datos</div>;

    const scatter = sigmaData.PuntosScatter || [];
    const resumen = sigmaData.ResumenPorMes || [];

    return (
      <div className='qc-report-content'>
        <div className='qc-report-card'>
          <h4 className='qc-report-title'>Distribución Sigma por Analito/Nivel</h4>
          {scatter.length === 0 ? (
            <div className='qc-reports-empty'>Sin datos en el período</div>
          ) : (
            <Chart
              dataSource={scatter.map((p, idx) => ({
                Idx: idx + 1,
                Etiqueta: `${p.AnalitoNombre} (N${p.Nivel})`,
                AnalitoNombre: p.AnalitoNombre,
                Nivel: p.Nivel,
                MaterialNivel: p.MaterialNivel,
                SixSigma: p.SixSigma,
                Clasificacion: p.Clasificacion,
              }))}
              height={400}
              customizePoint={(pt: any) => ({
                color: sigmaBandColor(pt.data?.SixSigma),
                hoverStyle: { color: sigmaBandColor(pt.data?.SixSigma) },
              })}>
              <ArgumentAxis title='Analito / Nivel' />
              <ValueAxis title='Sigma' visualRange={[0, Math.max(7, Math.ceil(Math.max(...scatter.map((s) => s.SixSigma || 0)) + 1))]}>
                <ConstantLine value={6} color={SIGMA_BAND_COLORS.WORLD} dashStyle='dash' width={1.2}><Label text='6σ' visible={true} /></ConstantLine>
                <ConstantLine value={5} color={SIGMA_BAND_COLORS.EXCELLENT} dashStyle='dash' width={1.2}><Label text='5σ' visible={true} /></ConstantLine>
                <ConstantLine value={4} color={SIGMA_BAND_COLORS.GOOD} dashStyle='dash' width={1.2}><Label text='4σ' visible={true} /></ConstantLine>
                <ConstantLine value={3} color={SIGMA_BAND_COLORS.MIN_ACCEPT} dashStyle='dash' width={1.2}><Label text='3σ' visible={true} /></ConstantLine>
                <ConstantLine value={2} color={SIGMA_BAND_COLORS.UNACCEPT} dashStyle='dash' width={1.2}><Label text='2σ' visible={true} /></ConstantLine>
              </ValueAxis>
              <Animation enabled={true} duration={600} easing='easeOutCubic' />
              <CommonSeriesSettings argumentField='Etiqueta' />
              <Series valueField='SixSigma' name='Sigma' type='scatter'>
                <Point size={14} symbol='circle' />
              </Series>
              <Legend visible={false} />
              <ChartTooltip enabled={true} customizeTooltip={(pt: any) => ({
                html: `<div style="padding:8px;">
                  <div><strong>${pt.point.data.AnalitoNombre}</strong></div>
                  <div>Nivel: ${pt.point.data.MaterialNivel} (${pt.point.data.Nivel})</div>
                  <div>Sigma: <strong style="color:${sigmaBandColor(pt.point.data.SixSigma)}">${pt.point.data.SixSigma?.toFixed(2)}</strong></div>
                  <div>Clasificación: ${pt.point.data.Clasificacion}</div>
                </div>`,
              })} />
            </Chart>
          )}
        </div>

        <div className='qc-report-card'>
          <h4 className='qc-report-title'>Resumen Mensual</h4>
          <DataGrid
            dataSource={resumen}
            showBorders={true}
            rowAlternationEnabled={true}
            hoverStateEnabled={true}
            columnAutoWidth={true}
            height={Math.min(400, 60 + resumen.length * 35)}>
            <Column dataField='Mes' caption='Mes' width={110} alignment='center' />
            <Column dataField='Sigma6' caption='≥ 6σ' alignment='center' cellRender={(c: any) => <span className='sigma-cell' style={{ background: SIGMA_BAND_COLORS.WORLD }}>{c.value}</span>} />
            <Column dataField='Sigma5' caption='5σ' alignment='center' cellRender={(c: any) => <span className='sigma-cell' style={{ background: SIGMA_BAND_COLORS.EXCELLENT }}>{c.value}</span>} />
            <Column dataField='Sigma4' caption='4σ' alignment='center' cellRender={(c: any) => <span className='sigma-cell' style={{ background: SIGMA_BAND_COLORS.GOOD, color: '#333' }}>{c.value}</span>} />
            <Column dataField='Sigma3' caption='3σ' alignment='center' cellRender={(c: any) => <span className='sigma-cell' style={{ background: SIGMA_BAND_COLORS.MIN_ACCEPT }}>{c.value}</span>} />
            <Column dataField='Sigma2' caption='2σ' alignment='center' cellRender={(c: any) => <span className='sigma-cell' style={{ background: SIGMA_BAND_COLORS.UNACCEPT }}>{c.value}</span>} />
            <Column dataField='SigmaMenor2' caption='< 2σ' alignment='center' cellRender={(c: any) => <span className='sigma-cell' style={{ background: SIGMA_BAND_COLORS.CRITICAL }}>{c.value}</span>} />
            <Column
              dataField='PorcentajeCumplimiento'
              caption='% Cumplimiento'
              alignment='center'
              cellRender={(c: any) => {
                const v = Number(c.value || 0);
                const color = v >= 80 ? '#4CAF50' : v >= 60 ? '#FF9800' : '#F44336';
                return <strong style={{ color }}>{v.toFixed(2)}%</strong>;
              }}
            />
          </DataGrid>
        </div>
      </div>
    );
  };

  // ─── Render: Resumen CV ───
  const renderCvTab = () => {
    if (!cvData) return <div className='qc-reports-empty'>Genere el informe para ver los datos</div>;
    const resumen = cvData.ResumenPorMes || [];
    return (
      <div className='qc-report-content'>
        <div className='qc-report-card'>
          <h4 className='qc-report-title'>Resumen CV Mensual</h4>
          <DataGrid
            dataSource={resumen}
            showBorders={true}
            rowAlternationEnabled={true}
            hoverStateEnabled={true}
            columnAutoWidth={true}
            height={Math.min(400, 60 + resumen.length * 35)}>
            <Column dataField='Mes' caption='Mes' width={110} alignment='center' />
            <Column dataField='CVOK' caption='OK CV' alignment='center' cellRender={(c: any) => <span className='sigma-cell' style={{ background: '#4CAF50' }}>{c.value}</span>} />
            <Column dataField='CVError' caption='ERROR CV' alignment='center' cellRender={(c: any) => <span className='sigma-cell' style={{ background: '#F44336' }}>{c.value}</span>} />
            <Column
              dataField='PorcentajeCumplimiento'
              caption='% Cumplimiento'
              alignment='center'
              cellRender={(c: any) => {
                const v = Number(c.value || 0);
                const color = v >= 80 ? '#4CAF50' : v >= 60 ? '#FF9800' : '#F44336';
                return <strong style={{ color }}>{v.toFixed(2)}%</strong>;
              }}
            />
          </DataGrid>
        </div>
      </div>
    );
  };

  // ─── Render: Información General ───
  const renderGeneralTab = () => {
    if (!generalData || generalData.length === 0) return <div className='qc-reports-empty'>Genere el informe para ver los datos</div>;
    return (
      <div className='qc-report-card'>
        <h4 className='qc-report-title'>Información General</h4>
        <DataGrid
          dataSource={generalData}
          keyExpr='ConfiguracionId'
          showBorders={true}
          rowAlternationEnabled={true}
          hoverStateEnabled={true}
          columnAutoWidth={true}
          height={500}>
          <HeaderFilter visible={true} />
          <Scrolling mode='virtual' />
          <Sorting mode='multiple' />
          <Column dataField='Fila' caption='#' width={50} alignment='center' />
          <Column dataField='AnalitoNombre' caption='Analito' minWidth={150} alignment='left' />
          <Column dataField='AnalitoDescripcion' caption='Descripción' minWidth={180} alignment='left' visible={false} />
          <Column dataField='Nivel' caption='N' width={50} alignment='center' />
          <Column dataField='NDatos' caption='# Datos' width={80} alignment='center' />
          <Column dataField='MediaMensual' caption='Media Mensual' width={110} alignment='center' dataType='number' format='#,##0.000' />
          <Column dataField='MediaVerdadera' caption='Media Verdadera' width={120} alignment='center' dataType='number' format='#,##0.000' />
          <Column dataField='SesgoMensual' caption='Sesgo Mensual (%)' width={130} alignment='center' dataType='number' format='#,##0.00' />
          <Column dataField='Sa' caption='Sa (%)' width={80} alignment='center' dataType='number' format='#,##0.00' />
          <Column dataField='IS' caption='IS' width={80} alignment='center' cellRender={(c: any) => {
            if (c.value == null) return <span style={{ color: '#aaa' }}>—</span>;
            return <strong style={{ color: indexColor(c.value) }}>{Number(c.value).toFixed(2)}</strong>;
          }} />
          <Column dataField='CVMensual' caption='CV Mensual (%)' width={120} alignment='center' dataType='number' format='#,##0.00' />
          <Column dataField='CVa' caption='CVa (%)' width={80} alignment='center' dataType='number' format='#,##0.00' />
          <Column dataField='ICV' caption='ICV' width={80} alignment='center' cellRender={(c: any) => {
            if (c.value == null) return <span style={{ color: '#aaa' }}>—</span>;
            return <strong style={{ color: indexColor(c.value) }}>{Number(c.value).toFixed(2)}</strong>;
          }} />
          <Column dataField='ETMensual' caption='ET Mensual' width={110} alignment='center' dataType='number' format='#,##0.00' />
          <Column dataField='ETa' caption='ETa (%)' width={80} alignment='center' dataType='number' format='#,##0.00' />
          <Column dataField='IET' caption='IET' width={80} alignment='center' cellRender={(c: any) => {
            if (c.value == null) return <span style={{ color: '#aaa' }}>—</span>;
            return <strong style={{ color: indexColor(c.value) }}>{Number(c.value).toFixed(2)}</strong>;
          }} />
          <Column dataField='SixSigma' caption='Six Sigma' width={100} alignment='center' cellRender={(c: any) => {
            if (c.value == null) return <span style={{ color: '#aaa' }}>—</span>;
            return <span className='sigma-cell' style={{ background: sigmaBandColor(c.value) }}>{Number(c.value).toFixed(2)}</span>;
          }} />
          <Column dataField='ESC' caption='ESC' width={80} alignment='center' dataType='number' format='#,##0.00' />
          <Column dataField='Clasificacion' caption='Clasificación' width={130} alignment='center' />
        </DataGrid>
      </div>
    );
  };

  // ─── Render: Indicadores (3 gráficos de barras) ───
  const renderIndicatorsTab = () => {
    if (!indicatorsData || indicatorsData.length === 0) return <div className='qc-reports-empty'>Genere el informe para ver los datos</div>;

    const buildSeriesData = (key: 'ICV' | 'IS' | 'IET') => {
      return indicatorsData.map((a) => {
        const row: any = { Analito: a.AnalitoNombre };
        a.Niveles.forEach((n) => {
          const v = (n as any)[key];
          row[`Nivel${n.Nivel}`] = v == null ? null : v;
        });
        return row;
      });
    };

    const renderIndicatorChart = (title: string, key: 'ICV' | 'IS' | 'IET') => {
      const data = buildSeriesData(key);
      const niveles = Array.from(new Set(indicatorsData.flatMap((a) => a.Niveles.map((n) => n.Nivel)))).sort();
      return (
        <div className='qc-report-card'>
          <h4 className='qc-report-title'>{title}</h4>
          <Chart
            dataSource={data}
            height={350}
            customizePoint={(pt: any) => {
              const v = pt.value;
              if (v == null) return {};
              return { color: v > 1 ? '#F44336' : '#4CAF50', hoverStyle: { color: v > 1 ? '#F44336' : '#4CAF50' } };
            }}>
            <ArgumentAxis title='Analito' />
            <ValueAxis title={key}>
              <ConstantLine value={1} color='#F44336' dashStyle='dash' width={1.5}><Label text='Límite 1.0' visible={true} /></ConstantLine>
            </ValueAxis>
            <Animation enabled={true} duration={600} />
            <CommonSeriesSettings argumentField='Analito' type='bar' />
            {niveles.map((n) => (
              <Series key={`n${n}`} valueField={`Nivel${n}`} name={`Nivel ${n}`} />
            ))}
            <Legend verticalAlignment='bottom' horizontalAlignment='center' />
            <ChartTooltip enabled={true} />
          </Chart>
        </div>
      );
    };

    return (
      <div className='qc-report-content'>
        {renderIndicatorChart('Índice de Coeficiente de Variación (ICV)', 'ICV')}
        {renderIndicatorChart('Índice de Sesgo (IS)', 'IS')}
        {renderIndicatorChart('Índice de Error Total (IET)', 'IET')}
      </div>
    );
  };

  // ─── Render: Histórico Sigma ───
  const renderSigmaHistoryTab = () => {
    if (!sigmaHistData || sigmaHistData.length === 0) return <div className='qc-reports-empty'>Genere el informe para ver los datos</div>;

    const months = Array.from(new Set(sigmaHistData.flatMap((r) => Object.keys(r.ValoresPorMes || {}))));

    return (
      <div className='qc-report-card'>
        <h4 className='qc-report-title'>Histórico Sigma por Mes</h4>
        <DataGrid
          dataSource={sigmaHistData}
          keyExpr='ConfiguracionId'
          showBorders={true}
          rowAlternationEnabled={true}
          hoverStateEnabled={true}
          columnAutoWidth={true}
          height={500}>
          <HeaderFilter visible={true} />
          <Scrolling mode='virtual' />
          <Sorting mode='multiple' />
          <Column dataField='Fila' caption='#' width={50} alignment='center' fixed={true} />
          <Column dataField='AnalitoNombre' caption='Analito' minWidth={160} alignment='left' fixed={true} />
          <Column dataField='Nivel' caption='Nivel' width={70} alignment='center' fixed={true} />
          {months.map((m) => (
            <Column
              key={m}
              caption={m}
              width={100}
              alignment='center'
              calculateCellValue={(rowData: any) => rowData?.ValoresPorMes?.[m] ?? null}
              cellRender={(c: any) => {
                const v = c.value;
                if (v == null) return <span style={{ color: '#ccc' }}>—</span>;
                const color = sigmaBandColor(v);
                return (
                  <span style={{ display: 'inline-block', padding: '3px 8px', border: `2px solid ${color}`, borderRadius: '6px', color, fontWeight: 700, minWidth: 50 }}>
                    {Number(v).toFixed(2)}
                  </span>
                );
              }}
            />
          ))}
        </DataGrid>
      </div>
    );
  };

  // ─── Render: Histórico CV ───
  const renderCvHistoryTab = () => {
    if (!cvHistData || cvHistData.length === 0) return <div className='qc-reports-empty'>Genere el informe para ver los datos</div>;

    const months = Array.from(new Set(cvHistData.flatMap((r) => Object.keys(r.ValoresPorMes || {}))));

    return (
      <div className='qc-report-card'>
        <h4 className='qc-report-title'>Histórico CV por Mes</h4>
        <DataGrid
          dataSource={cvHistData}
          keyExpr='ConfiguracionId'
          showBorders={true}
          rowAlternationEnabled={true}
          hoverStateEnabled={true}
          columnAutoWidth={true}
          height={500}>
          <HeaderFilter visible={true} />
          <Scrolling mode='virtual' />
          <Sorting mode='multiple' />
          <Column dataField='Fila' caption='#' width={50} alignment='center' fixed={true} />
          <Column dataField='AnalitoNombre' caption='Analito' minWidth={160} alignment='left' fixed={true} />
          <Column dataField='Nivel' caption='Nivel' width={70} alignment='center' fixed={true} />
          <Column dataField='CVa' caption='CVa (%)' width={90} alignment='center' dataType='number' format='#,##0.00' fixed={true} />
          {months.map((m) => (
            <Column
              key={m}
              caption={m}
              width={110}
              alignment='center'
              calculateCellValue={(rowData: any) => rowData?.ValoresPorMes?.[m]?.CV ?? null}
              cellRender={(c: any) => {
                const cell = c.data?.ValoresPorMes?.[m];
                if (!cell || cell.CV == null) return <span style={{ color: '#ccc' }}>—</span>;
                const color = cell.EsAceptable ? '#4CAF50' : '#F44336';
                return (
                  <span style={{ display: 'inline-block', padding: '3px 8px', border: `2px solid ${color}`, borderRadius: '6px', color, fontWeight: 700, minWidth: 60 }}>
                    {Number(cell.CV).toFixed(2)}
                  </span>
                );
              }}
            />
          ))}
        </DataGrid>
      </div>
    );
  };

  // ─── Render: Planificación ───
  const renderPlanningTab = () => {
    if (!planningData || planningData.length === 0) return <div className='qc-reports-empty'>Genere el informe para ver los datos</div>;
    return (
      <div className='qc-report-card'>
        <h4 className='qc-report-title'>Planificación de Control de Calidad</h4>
        <DataGrid
          dataSource={planningData}
          keyExpr='ConfiguracionId'
          showBorders={true}
          rowAlternationEnabled={true}
          hoverStateEnabled={true}
          columnAutoWidth={true}
          height={500}>
          <HeaderFilter visible={true} />
          <Scrolling mode='virtual' />
          <Sorting mode='multiple' />
          <Column dataField='Fila' caption='#' width={50} alignment='center' />
          <Column dataField='AnalitoNombre' caption='Analito' minWidth={160} alignment='left' />
          <Column dataField='AnalitoDescripcion' caption='Descripción' minWidth={180} alignment='left' visible={false} />
          <Column dataField='Nivel' caption='Nivel' width={80} alignment='center' />
          <Column
            dataField='SixSigma'
            caption='Sigma'
            width={110}
            alignment='center'
            cellRender={(c: any) => {
              if (c.value == null) return <span style={{ color: '#aaa' }}>—</span>;
              return <span className='sigma-cell' style={{ background: sigmaBandColor(c.value) }}>{Number(c.value).toFixed(2)}</span>;
            }}
          />
          <Column
            dataField='Clasificacion'
            caption='Clasificación'
            width={150}
            alignment='center'
            cellRender={(c: any) => {
              const sig = c.data?.SixSigma;
              const color = sigmaBandColor(sig);
              return (
                <span style={{ background: color, color: 'white', padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 700 }}>
                  {String(c.value || '').replace('_', ' ')}
                </span>
              );
            }}
          />
          <Column dataField='ReglasWestgard' caption='Reglas Westgard recomendadas' minWidth={260} alignment='left' />
        </DataGrid>
      </div>
    );
  };

  const tabs: { key: ReportTab; label: string }[] = [
    { key: 'sigma', label: 'Resumen Sigma' },
    { key: 'cv', label: 'Resumen CV' },
    { key: 'general', label: 'Información General' },
    { key: 'indicators', label: 'Indicadores' },
    { key: 'sigmaHist', label: 'Histórico Sigma' },
    { key: 'cvHist', label: 'Histórico CV' },
    { key: 'planning', label: 'Planificación QC' },
  ];

  const activeTabIndex = tabs.findIndex((t) => t.key === activeTab);

  return (
    <div className='qc-reports-container' style={{ position: 'relative' }}>
      <LoadPanel
        visible={isLoading || isExporting}
        message={isExporting ? 'Generando PDF consolidado...' : 'Cargando informe...'}
        showIndicator={true}
        showPane={true}
        shading={true}
        shadingColor='rgba(255,255,255,0.7)'
        position={{ of: '.qc-reports-container' }}
      />

      {/* Filtros */}
      <div className='qc-reports-filters'>
        <div className='qc-reports-filter-row'>
          <div className='qc-reports-filter-item'>
            <label>Desde *</label>
            <DateBox value={fechaDesde} onValueChanged={(e) => setFechaDesde(e.value)} displayFormat='dd/MM/yyyy' height={32} />
          </div>
          <div className='qc-reports-filter-item'>
            <label>Hasta *</label>
            <DateBox value={fechaHasta} onValueChanged={(e) => setFechaHasta(e.value)} displayFormat='dd/MM/yyyy' height={32} />
          </div>
          <div className='qc-reports-filter-item'>
            <label>Analizador</label>
            <SelectBox
              dataSource={analizadorOptions}
              valueExpr='id'
              displayExpr='nombre'
              value={analizadorId}
              onValueChanged={(e) => setAnalizadorId(e.value)}
              placeholder='Todos'
              showClearButton={true}
              searchEnabled={true}
              height={32}
            />
          </div>
          <div className='qc-reports-filter-item'>
            <label>Lote</label>
            <SelectBox
              dataSource={loteOptions}
              valueExpr='id'
              displayExpr='nombre'
              value={loteId}
              onValueChanged={(e) => setLoteId(e.value)}
              placeholder='Todos'
              showClearButton={true}
              searchEnabled={true}
              height={32}
            />
          </div>
          <div className='qc-reports-filter-item'>
            <label>Analito</label>
            <SelectBox
              dataSource={analitoOptions}
              valueExpr='id'
              displayExpr='nombre'
              value={analitoId}
              onValueChanged={(e) => setAnalitoId(e.value)}
              placeholder='Todos'
              showClearButton={true}
              searchEnabled={true}
              height={32}
            />
          </div>
          <div className='qc-reports-filter-action'>
            <Button text='Generar Informe' icon='find' type='default' stylingMode='contained' onClick={handleGenerate} />
            <Button
              text='Exportar PDF'
              icon='exportpdf'
              type='success'
              stylingMode='contained'
              onClick={handleExportPdf}
              disabled={isExporting}
              hint='Descarga un PDF con todos los informes (consolidado)'
            />
          </div>
        </div>
      </div>

      {/* Sub-tabs de informes */}
      <TabPanel
        className='sub-tab-pills qc-reports-tabpanel'
        animationEnabled={true}
        swipeEnabled={false}
        selectedIndex={activeTabIndex < 0 ? 0 : activeTabIndex}
        onSelectedIndexChange={(idx: number) => {
          if (tabs[idx]) setActiveTab(tabs[idx].key);
        }}>
        {tabs.map((t) => (
          <TabItem key={t.key} title={t.label}>
            <ScrollView width='100%' height='100%'>
              <div className='qc-report-tab-body'>
                {!hasSearched ? (
                  <div className='qc-reports-empty'>Configure los filtros y presione <strong>Generar Informe</strong> para visualizar los datos.</div>
                ) : (
                  <>
                    {t.key === 'sigma' && renderSigmaTab()}
                    {t.key === 'cv' && renderCvTab()}
                    {t.key === 'general' && renderGeneralTab()}
                    {t.key === 'indicators' && renderIndicatorsTab()}
                    {t.key === 'sigmaHist' && renderSigmaHistoryTab()}
                    {t.key === 'cvHist' && renderCvHistoryTab()}
                    {t.key === 'planning' && renderPlanningTab()}
                  </>
                )}
              </div>
            </ScrollView>
          </TabItem>
        ))}
      </TabPanel>
    </div>
  );
};

export default QcReports;
